import { isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment, { Moment } from 'moment'
import { Person } from 'people/interface'
import useFetchRefactored from 'civic-champs-shared/api/hooks/useFetchRefactored'

const DEFAULT_PAGE_SIZE = 25

export type PeopleDateRange = {
  startDate: Moment | null
  endDate: Moment | null
} | null

export type PeopleTableSort = { id: string; desc: boolean }[]

const SORT_COLUMN_TO_API: Record<string, string> = {
  id: 'id',
  firstName: 'givenName',
  lastName: 'familyName',
  email: 'email',
  createdAt: 'createdAt',
}

const formatDateParam = (value?: Moment | null) => (value ? moment(value).format('YYYY-MM-DD') : undefined)

const buildFilterParams = ({ search, dateRange }: { search: string; dateRange: PeopleDateRange }) => {
  const trimmedSearch = search.trim()
  return {
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
    ...(formatDateParam(dateRange?.startDate) && {
      createdAfter: formatDateParam(dateRange?.startDate),
    }),
    ...(formatDateParam(dateRange?.endDate) && {
      createdBefore: formatDateParam(dateRange?.endDate),
    }),
  }
}

export const useFetchPeople = () => {
  const [people, setPeople] = useState<Person[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [dateRange, setDateRange] = useState<PeopleDateRange>(null)
  const [sortBy, setSortBy] = useState<PeopleTableSort>([{ id: 'id', desc: false }])
  const [request] = useFetchRefactored()
  const fetchSeq = useRef(0)

  const refetch = useCallback(async () => {
    const seq = ++fetchSeq.current
    setLoading(true)
    const filterParams = buildFilterParams({ search, dateRange })
    const [{ id, desc }] = sortBy
    const sortParams = {
      sortBy: SORT_COLUMN_TO_API[id] || 'id',
      sortDir: desc ? 'desc' : 'asc',
    }

    try {
      const [{ count }, page] = await Promise.all([
        request({
          url: '/people-count',
          config: { queryStringParameters: filterParams },
        }) as Promise<{ count: number }>,
        request({
          url: '/people',
          config: {
            queryStringParameters: {
              ...filterParams,
              ...sortParams,
              limit: pageSize,
              offset: pageIndex * pageSize,
            },
          },
        }) as Promise<Person[]>,
      ])
      if (seq !== fetchSeq.current) {
        return
      }
      setTotalCount(count)
      setPeople(page)
    } finally {
      if (seq === fetchSeq.current) {
        setLoading(false)
      }
    }
  }, [dateRange, pageIndex, pageSize, request, search, sortBy])

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1)
    if (pageIndex > maxPage) {
      setPageIndex(maxPage)
    }
  }, [totalCount, pageSize, pageIndex])

  const onSearchChange = useCallback((query: string) => {
    setSearch(query)
    setPageIndex(0)
  }, [])

  const onDateRangeChange = useCallback((range: PeopleDateRange) => {
    setDateRange(range)
    setPageIndex(0)
  }, [])

  const onSortChange = useCallback((nextSortBy: PeopleTableSort) => {
    const normalized = nextSortBy.length ? nextSortBy : [{ id: 'id', desc: false }]
    setSortBy(prev => {
      if (isEqual(prev, normalized)) {
        return prev
      }
      setPageIndex(0)
      return normalized
    })
  }, [])

  const handlePageSizeChange = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize)
    setPageIndex(0)
  }, [])

  return useMemo(
    () => ({
      people,
      totalCount,
      loading,
      pageIndex,
      setPageIndex,
      pageSize,
      setPageSize: handlePageSizeChange,
      search,
      dateRange,
      sortBy,
      onSearchChange,
      onDateRangeChange,
      onSortChange,
      refetch,
    }),
    [
      people,
      totalCount,
      loading,
      pageIndex,
      pageSize,
      handlePageSizeChange,
      search,
      dateRange,
      sortBy,
      onSearchChange,
      onDateRangeChange,
      onSortChange,
      refetch,
    ],
  )
}
